import React, { memo } from 'react';
import CollectionHero from './CollectionHero';
import './CollectionCard.css';

/**
 * CollectionCard component displays a collection in a grid.
 * Similar to list-preview-card styling with hero image and metadata.
 */
const CollectionCard = memo(function CollectionCard({
  collection,
  lists = [], // Pre-fetched lists passed from parent
  onClick,
  onEdit,
  isExpanded = false,
  isReorderMode = false,
  showPrivacyBadge = true,
}) {
  const listCount = lists.length;

  const handleClick = () => {
    if (isReorderMode) return;
    if (onClick) {
      onClick(collection);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(collection);
    }
  };

  return (
    <div
      className={`collection-card ${isExpanded ? 'collection-card--expanded' : ''} ${isReorderMode ? 'disabled' : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={isReorderMode ? -1 : 0}
      aria-label={`${collection.title}, ${listCount} ${listCount === 1 ? 'list' : 'lists'}`}
    >
      {/* Hero section */}
      <div className="collection-card__hero">
        <CollectionHero
          lists={lists}
          customHeroUrl={collection.heroPhotoUrl}
          altText={collection.title}
        />

        {/* Privacy badge */}
        {showPrivacyBadge && collection.visibility === 'private' && (
          <div className="privacy-badge" title="Private collection">
            <svg
              className="privacy-badge-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
        )}

        {/* Expand indicator */}
        <div className={`collection-card__expand-indicator ${isExpanded ? 'expanded' : ''}`}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>

      {/* Content section */}
      <div className="collection-card__content">
        <div className="collection-card__content-main">
          <h3 className="collection-card__title">{collection.title}</h3>
          <p className="collection-card__meta">
            {listCount} {listCount === 1 ? 'list' : 'lists'}
          </p>
        </div>
        {onEdit && (
          <button
            className="collection-card__edit-btn"
            onClick={handleEditClick}
            aria-label="Edit collection"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
});

export default CollectionCard;
